import {
  ChangeDetectionStrategy,
  Component,
  AfterViewInit,
  OnDestroy,
  signal,
  input,
  output,
  effect,
  viewChild,
  ElementRef,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as atlas from 'azure-maps-control';

export interface SimpleMapLocation {
  latitude: number;
  longitude: number;
}

@Component({
  selector: 'app-simple-map',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="relative rounded-md" data-cy="simple-map">
      <div #mapContainer class="w-full h-96 border rounded"></div>

      @if (selectionModeActive() && location()) {
        <div class="absolute bottom-2 left-2">
          <div class="bg-white p-2 rounded text-sm">
            <span class="text-dark">Klik binnen de straal om de locatie te verplaatsen</span>
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        margin: 1rem 0;
      }

      ::ng-deep {
        #atlas-map-shortcuts,
        #atlas-map-state,
        .atlas-control-container,
        #atlas-map-style {
          display: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SimpleMapComponent implements AfterViewInit, OnDestroy {
  readonly mapContainer = viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');

  // Inputs
  readonly location = input<SimpleMapLocation | null>(null);
  readonly geoRadius = input<number>(0);
  readonly allowMapSelection = input<boolean>(false);

  // Outputs
  readonly locationSelected = output<SimpleMapLocation>();

  // State
  readonly selectionModeActive = signal(false);

  // Private
  #map: atlas.Map | null = null;
  #datasource: atlas.source.DataSource | null = null;
  #radiusDataSource: atlas.source.DataSource | null = null;
  #mapClickEventListener: any;
  #radiusCenter: atlas.data.Position | null = null;
  #locationUpdatedFromSelection = false;

  constructor() {
    // React to location changes
    effect(() => {
      const loc = this.location();
      if (loc && this.#map && this.#datasource) {
        this.renderLocation();
      }
    });

    // React to geoRadius changes
    effect(() => {
      this.geoRadius();
      if (this.#map && this.#radiusDataSource) {
        this.renderRadius();
        this.fitRadiusBounds();
      }
    });
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    if (this.#mapClickEventListener && this.#map) {
      this.#map.events.remove('click', this.#mapClickEventListener);
    }

    if (this.#map) {
      this.#map.dispose();
      this.#map = null;
    }
  }

  private initMap(): void {
    this.#map = new atlas.Map(this.mapContainer().nativeElement, {
      center: [5.2913, 52.1326], // Nederland centraal
      zoom: 7,
      view: 'Auto',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: '65TAwBgYcORpiWAKdmSK7ek1lv75sxr2-nWJuAGGBb0',
      },
      style: 'grayscale_light',
      debugMode: false,
      showLogo: false,
      showFeedbackLink: false,
    });

    this.#map.events.add('ready', () => {
      if (!this.#map) return;

      this.#datasource = new atlas.source.DataSource();
      this.#map.sources.add(this.#datasource);

      this.#radiusDataSource = new atlas.source.DataSource();
      this.#map.sources.add(this.#radiusDataSource);

      this.#map.layers.add([
        // Radius circle fill layer
        new atlas.layer.PolygonLayer(this.#radiusDataSource, null, {
          fillColor: 'rgba(0, 120, 255, 0.2)',
          fillOpacity: 0.5,
        }),

        // Radius circle outline layer
        new atlas.layer.LineLayer(this.#radiusDataSource, null, {
          strokeColor: '#38cab3',
          strokeWidth: 2,
          strokeDashArray: [2, 2],
        }),

        // Marker layer - simple pin
        new atlas.layer.SymbolLayer(this.#datasource, null, {
          iconOptions: {
            image: 'marker-blue',
            allowOverlap: true,
            ignorePlacement: true,
          },
          filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']],
        }),
      ]);

      // Add click event handler for location selection
      this.#mapClickEventListener = this.#map.events.add('click', (e: atlas.MapMouseEvent) => {
        if (this.allowMapSelection() && this.selectionModeActive()) {
          const clickedPosition = new atlas.data.Position(e.position[0], e.position[1]);

          if (this.isPointInRadius(clickedPosition)) {
            this.#locationUpdatedFromSelection = true;

            this.locationSelected.emit({
              latitude: e.position[1],
              longitude: e.position[0],
            });
          }
        }
      });

      // Render initial location if available
      this.renderLocation();
    });
  }

  private isPointInRadius(point: atlas.data.Position): boolean {
    if (!this.#radiusCenter) return false;

    const distance = atlas.math.getDistanceTo(this.#radiusCenter, point, 'meters');
    return distance <= this.geoRadius();
  }

  private renderRadius(): void {
    if (!this.#map || !this.#radiusDataSource) return;

    const radius = this.geoRadius();
    if (radius <= 0 || !this.#radiusCenter) {
      this.#radiusDataSource.clear();
      this.selectionModeActive.set(false);
      return;
    }

    this.#radiusDataSource.clear();

    try {
      const circleCoords = atlas.math.getRegularPolygonPath(this.#radiusCenter, radius, 36, 'meters');

      this.#radiusDataSource.add(new atlas.Shape(new atlas.data.Polygon([circleCoords])));
      this.selectionModeActive.set(true);
    } catch (error) {
      console.error('Error creating radius circle:', error);
    }
  }

  private fitRadiusBounds(): void {
    if (!this.#map || !this.#radiusCenter || this.geoRadius() <= 0) return;

    const circleCoords = atlas.math.getRegularPolygonPath(this.#radiusCenter, this.geoRadius(), 36, 'meters');
    const bounds = atlas.data.BoundingBox.fromPositions(circleCoords);
    this.#map.setCamera({
      bounds,
      padding: 40,
      maxZoom: 13,
    });
  }

  private renderLocation(): void {
    const loc = this.location();
    if (!loc || !loc.latitude || !loc.longitude || !this.#map || !this.#datasource) return;

    this.#datasource.clear();

    const coordinates: [number, number] = [loc.longitude, loc.latitude];

    // Update radius center (unless location was updated from selection)
    if (!this.#locationUpdatedFromSelection) {
      this.#radiusCenter = new atlas.data.Position(coordinates[0], coordinates[1]);
      this.renderRadius();
    }
    this.#locationUpdatedFromSelection = false;

    // Add marker
    this.#datasource.add(
      new atlas.data.Feature(new atlas.data.Point(coordinates), {
        title: '',
      })
    );

    // Center map on location, zoom to fit radius if needed
    if (this.geoRadius() > 0 && this.#radiusCenter) {
      this.fitRadiusBounds();
    } else {
      this.#map.setCamera({
        center: coordinates,
        zoom: this.#map.getCamera().zoom < 13 ? 13 : this.#map.getCamera().zoom,
      });
    }
  }
}
